import { apiClient } from './axios';

// ============================================================================
// PHYSICAL METRICS
// ============================================================================

export interface PhysicalMetric {
  id: string;
  weight: number;
  weightUnit: 'kg' | 'lbs';
  height?: number;
  heightUnit?: 'cm' | 'in';
  bodyConditionScore?: number;
  recordedAt: string;
  notes?: string;
  recordedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePhysicalMetricRequest {
  weight: number;
  weightUnit: 'kg' | 'lbs';
  height?: number;
  heightUnit?: 'cm' | 'in';
  bodyConditionScore?: number;
  recordedAt: string;
  notes?: string;
  recordedBy?: string;
}

export interface CreatePhysicalMetricResponse {
  message: string;
  metric: PhysicalMetric;
}

export interface GetPhysicalMetricsResponse {
  metrics: PhysicalMetric[];
}

// Create a new physical metric
export async function createPhysicalMetric(
  petId: string,
  data: CreatePhysicalMetricRequest
): Promise<CreatePhysicalMetricResponse> {
  const response = await apiClient.post<CreatePhysicalMetricResponse>(
    `/pro/pets/${petId}/physical-metrics`,
    data
  );
  return response.data;
}

// Get all physical metrics for a pet
export async function getPhysicalMetrics(
  petId: string
): Promise<GetPhysicalMetricsResponse> {
  const response = await apiClient.get<GetPhysicalMetricsResponse>(
    `/pro/pets/${petId}/physical-metrics`
  );
  return response.data;
}

// ============================================================================
// PET UPDATES
// ============================================================================

export interface UpdatePetRequest {
  name?: string;
  species?: 'dog' | 'cat' | 'bird' | 'reptile' | 'other';
  breed?: string;
  dateOfBirth?: string;
  approximateAge?: string;
  gender?: 'male' | 'female';
  neuteredSpayed?: boolean;
  color?: string;
  microchipId?: string;
  country?: string;
  city?: string;
  motherDid?: string;
  fatherDid?: string;
  medicalConditions?: string;
  allergies?: string;
  currentMedications?: string;
  dietaryRequirements?: string;
  vetClinic?: string;
  temperament?: string[];
  groomingTolerance?: string;
  trainingLevel?: string;
  exerciseNeeds?: string;
  boardingSuitability?: string;
  shareHealthData?: boolean;
  shareGroomingNotes?: boolean;
  emergencyAccess?: boolean;
}

export interface PetResponse {
  id: string;
  did?: string;
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'reptile' | 'other';
  breed: string;
  dateOfBirth?: string;
  gender: string;
  color?: string;
  microchipId?: string;
  country?: string;
  city?: string;
  createdAt: string;
  updatedAt: string;
}

// Update a pet
export async function updatePet(
  id: string,
  data: Partial<UpdatePetRequest>
): Promise<{ message: string; pet: PetResponse }> {
  const response = await apiClient.put<{ message: string; pet: PetResponse }>(
    `/pro/pets/${id}`,
    data
  );
  return response.data;
}

// ============================================================================
// VACCINATIONS
// ============================================================================

export interface Vaccination {
  id: string;
  vaccineName: string;
  dateAdministered: string;
  expiryDate: string;
  boosterDate?: string;
  administeredBy: string;
  clinicName: string;
  batchNumber: string;
  verified: boolean;
  credentialIssued: boolean;
  documentHash?: string;
  notes?: string;
  txSignature?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVaccinationRequest {
  vaccineName: string;
  dateAdministered: string;
  expiryDate: string;
  boosterDate?: string;
  administeredBy: string;
  clinicName: string;
  batchNumber: string;
  verified?: boolean;
  notes?: string;
}

export interface UpdateVaccinationRequest {
  vaccineName?: string;
  dateAdministered?: string;
  expiryDate?: string;
  boosterDate?: string | null;
  administeredBy?: string;
  clinicName?: string;
  batchNumber?: string;
  verified?: boolean;
  notes?: string | null;
}

export interface CreateVaccinationResponse {
  message: string;
  vaccination: Vaccination;
}

export interface GetVaccinationsResponse {
  vaccinations: Vaccination[];
}

// Create a new vaccination
export async function createVaccination(
  petId: string,
  data: CreateVaccinationRequest
): Promise<CreateVaccinationResponse> {
  const response = await apiClient.post<CreateVaccinationResponse>(
    `/pro/pets/${petId}/vaccinations`,
    data
  );
  return response.data;
}

// Get all vaccinations for a pet
export async function getVaccinations(
  petId: string
): Promise<GetVaccinationsResponse> {
  const response = await apiClient.get<GetVaccinationsResponse>(
    `/pro/pets/${petId}/vaccinations`
  );
  return response.data;
}

// Get a single vaccination by ID
export async function getVaccinationById(
  petId: string,
  vaccinationId: string
): Promise<{ vaccination: Vaccination }> {
  const response = await apiClient.get<{ vaccination: Vaccination }>(
    `/pro/pets/${petId}/vaccinations/${vaccinationId}`
  );
  return response.data;
}

// Update a vaccination
export async function updateVaccination(
  petId: string,
  vaccinationId: string,
  data: UpdateVaccinationRequest
): Promise<{ message: string; vaccination: Vaccination }> {
  const response = await apiClient.put<{
    message: string;
    vaccination: Vaccination;
  }>(`/pro/pets/${petId}/vaccinations/${vaccinationId}`, data);
  return response.data;
}

// Delete a vaccination
export async function deleteVaccination(
  petId: string,
  vaccinationId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pro/pets/${petId}/vaccinations/${vaccinationId}`
  );
  return response.data;
}

// ============================================================================
// GROOMING RECORDS
// ============================================================================

export type GroomingServiceType =
  | 'Full Grooming Service'
  | 'Basic Bath & Brush'
  | 'Nail Trim Only'
  | 'De-shedding Treatment'
  | 'Puppy/Kitten First Groom'
  | 'Other';

export type GroomingService =
  | 'Bath'
  | 'Haircut'
  | 'Nail Trim'
  | 'Ear Cleaning'
  | 'Brush'
  | 'De-shedding'
  | 'Teeth Brushing'
  | 'Anal Gland Expression';

export interface GroomingRecord {
  id: string;
  serviceType: GroomingServiceType;
  serviceDate: string;
  groomerName: string;
  salonName: string;
  servicesProvided: GroomingService[];
  coatCondition: string;
  behavioralObservations: string;
  groomerNotes?: string;
  verified: boolean;
  txSignature?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroomingRecordRequest {
  serviceType: GroomingServiceType;
  serviceDate: string;
  groomerName: string;
  salonName: string;
  servicesProvided: GroomingService[];
  coatCondition: string;
  behavioralObservations: string;
  groomerNotes?: string;
  verified?: boolean;
}

export interface UpdateGroomingRecordRequest {
  serviceType?: GroomingServiceType;
  serviceDate?: string;
  groomerName?: string;
  salonName?: string;
  servicesProvided?: GroomingService[];
  coatCondition?: string;
  behavioralObservations?: string;
  groomerNotes?: string | null;
  verified?: boolean;
}

export interface CreateGroomingRecordResponse {
  message: string;
  groomingRecord: GroomingRecord;
}

export interface GetGroomingRecordsResponse {
  groomingRecords: GroomingRecord[];
}

// Create a new grooming record
export async function createGroomingRecord(
  petId: string,
  data: CreateGroomingRecordRequest
): Promise<CreateGroomingRecordResponse> {
  const response = await apiClient.post<CreateGroomingRecordResponse>(
    `/pro/pets/${petId}/grooming`,
    data
  );
  return response.data;
}

// Get all grooming records for a pet
export async function getGroomingRecords(
  petId: string
): Promise<GetGroomingRecordsResponse> {
  const response = await apiClient.get<GetGroomingRecordsResponse>(
    `/pro/pets/${petId}/grooming`
  );
  return response.data;
}

// Get a single grooming record by ID
export async function getGroomingRecordById(
  petId: string,
  groomingId: string
): Promise<{ groomingRecord: GroomingRecord }> {
  const response = await apiClient.get<{ groomingRecord: GroomingRecord }>(
    `/pro/pets/${petId}/grooming/${groomingId}`
  );
  return response.data;
}

// Update a grooming record
export async function updateGroomingRecord(
  petId: string,
  groomingId: string,
  data: UpdateGroomingRecordRequest
): Promise<{ message: string; groomingRecord: GroomingRecord }> {
  const response = await apiClient.put<{
    message: string;
    groomingRecord: GroomingRecord;
  }>(`/pro/pets/${petId}/grooming/${groomingId}`, data);
  return response.data;
}

// Delete a grooming record
export async function deleteGroomingRecord(
  petId: string,
  groomingId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pro/pets/${petId}/grooming/${groomingId}`
  );
  return response.data;
}

// ============================================================================
// MEDICAL UPDATES
// ============================================================================

export type MedicalUpdateType =
  | 'Diagnosis'
  | 'Treatment'
  | 'Prescription'
  | 'Recovery';

export type MedicalUpdateStatus = 'Active' | 'Ongoing' | 'Resolved';

export interface MedicalUpdate {
  id: string;
  updateType: MedicalUpdateType;
  title: string;
  date: string;
  veterinarian: string;
  clinicName: string;
  description: string;
  medications?: string[];
  status: MedicalUpdateStatus;
  followUpDate?: string;
  verified: boolean;
  documentHash?: string;
  txSignature?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMedicalUpdateRequest {
  updateType: MedicalUpdateType;
  title: string;
  date: string;
  veterinarian: string;
  clinicName: string;
  description: string;
  medications?: string[];
  status: MedicalUpdateStatus;
  followUpDate?: string;
  verified?: boolean;
}

export interface UpdateMedicalUpdateRequest {
  updateType?: MedicalUpdateType;
  title?: string;
  date?: string;
  veterinarian?: string;
  clinicName?: string;
  description?: string;
  medications?: string[] | null;
  status?: MedicalUpdateStatus;
  followUpDate?: string | null;
  verified?: boolean;
}

export interface CreateMedicalUpdateResponse {
  message: string;
  medicalUpdate: MedicalUpdate;
}

export interface GetMedicalUpdatesResponse {
  medicalUpdates: MedicalUpdate[];
}

// Create a new medical update
export async function createMedicalUpdate(
  petId: string,
  data: CreateMedicalUpdateRequest
): Promise<CreateMedicalUpdateResponse> {
  const response = await apiClient.post<CreateMedicalUpdateResponse>(
    `/pro/pets/${petId}/medical-updates`,
    data
  );
  return response.data;
}

// Get all medical updates for a pet
export async function getMedicalUpdates(
  petId: string
): Promise<GetMedicalUpdatesResponse> {
  const response = await apiClient.get<GetMedicalUpdatesResponse>(
    `/pro/pets/${petId}/medical-updates`
  );
  return response.data;
}

// Get a single medical update by ID
export async function getMedicalUpdateById(
  petId: string,
  medicalUpdateId: string
): Promise<{ medicalUpdate: MedicalUpdate }> {
  const response = await apiClient.get<{ medicalUpdate: MedicalUpdate }>(
    `/pro/pets/${petId}/medical-updates/${medicalUpdateId}`
  );
  return response.data;
}

// Update a medical update
export async function updateMedicalUpdate(
  petId: string,
  medicalUpdateId: string,
  data: UpdateMedicalUpdateRequest
): Promise<{ message: string; medicalUpdate: MedicalUpdate }> {
  const response = await apiClient.put<{
    message: string;
    medicalUpdate: MedicalUpdate;
  }>(`/pro/pets/${petId}/medical-updates/${medicalUpdateId}`, data);
  return response.data;
}

// Delete a medical update
export async function deleteMedicalUpdate(
  petId: string,
  medicalUpdateId: string
): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(
    `/pro/pets/${petId}/medical-updates/${medicalUpdateId}`
  );
  return response.data;
}

// ============================================================================
// SIGNED DOCUMENT URLs (for social post images etc.)
// ============================================================================

// Fetch a file from R2 via the backend and return a blob URL for display.
export async function getSignedUrl(
  key: string,
  disposition: 'inline' | 'attachment' = 'inline'
): Promise<{ url: string; filename?: string; contentType?: string }> {
  const encodedKey = encodeURIComponent(key);
  const response = await apiClient.get(`/pro/documents/${encodedKey}/download`, {
    params: { disposition },
    responseType: 'blob',
  });

  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const contentType = blob.type || undefined;

  const cdHeader = response.headers?.['content-disposition'] as string | undefined;
  let filename: string | undefined;
  if (cdHeader) {
    const match = cdHeader.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }

  return { url, filename, contentType };
}
